
import Api from "@/api/userService";
import routerName from "@/router/routerName";
import { defineComponent, onMounted, ref, nextTick, h, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import * as Icons from "@ant-design/icons-vue";
import * as ElementPlusIcons from "@element-plus/icons-vue";
import { ElIcon } from "element-plus";
export default defineComponent({
  name: "HomeMenu",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const currentPath = route.path;
    const openKeys = ref([route.matched[0].path]);
    const selectedKeys = ref([route.path.substr(1)]);
    const menuItems = ref();

    // Element Plus 图标名称映射（el-icon-xxx -> IconName）
    const elIconMap: Record<string, string> = {
      "el-icon-house": "House",
      "el-icon-user": "User",
      "el-icon-aim": "Aim",
      "el-icon-help": "QuestionFilled",
      "el-icon-setting": "Setting",
    };

    // 路由配置中的图标名称映射（处理 car-filled 等格式）
    // 格式: "原始名称" -> ["ElementPlus图标名", "AntDesign图标名"] 或直接字符串
    const routeIconMap: Record<
      string,
      { el?: string; antd?: string } | string
    > = {
      "car-filled": { el: "Van", antd: "CarOutlined" },
      "contacts-filled": { el: "User", antd: "ContactsOutlined" },
      "setting-filled": { el: "Setting", antd: "SettingOutlined" },
      HomeFilled: { antd: "HomeFilled", el: "House" },
    };

    // 获取图标组件
    const getIconComponent = (icon: any) => {
      if (!icon) return null;
      if (typeof icon === "string") {
        // 如果是 Element Plus 的图标类名（el-icon-开头），转换为对应的图标组件
        if (icon.startsWith("el-icon-")) {
          const iconName = elIconMap[icon];
          if (iconName) {
            const IconComponent =
              ElementPlusIcons[iconName as keyof typeof ElementPlusIcons];
            if (IconComponent) {
              // 包装在 el-icon 组件中
              return () => h(ElIcon, { size: 16 }, () => h(IconComponent));
            }
          }
          // 如果找不到映射，尝试直接使用类名作为后备方案
          return () => h("i", { class: icon });
        }

        // 先检查路由图标映射
        const iconMapping = routeIconMap[icon];
        if (iconMapping) {
          if (typeof iconMapping === "string") {
            // 直接映射到字符串名称
            const ElIconComponent =
              ElementPlusIcons[iconMapping as keyof typeof ElementPlusIcons];
            if (ElIconComponent) {
              return () => h(ElIcon, { size: 16 }, () => h(ElIconComponent));
            }
            const IconComponent = Icons[iconMapping as keyof typeof Icons];
            if (IconComponent) {
              return IconComponent;
            }
          } else {
            // 对象映射，优先使用 Element Plus，然后 Ant Design
            if (iconMapping.el) {
              const ElIconComponent =
                ElementPlusIcons[
                  iconMapping.el as keyof typeof ElementPlusIcons
                ];
              if (ElIconComponent) {
                return () => h(ElIcon, { size: 16 }, () => h(ElIconComponent));
              }
            }
            if (iconMapping.antd) {
              const IconComponent =
                Icons[iconMapping.antd as keyof typeof Icons];
              if (IconComponent) {
                return IconComponent;
              }
            }
          }
        }

        // 如果是字符串，先尝试从 Ant Design Icons 中获取组件
        const IconComponent = Icons[icon as keyof typeof Icons];
        if (IconComponent) {
          return IconComponent;
        }
        // 尝试从 Element Plus Icons 中获取组件（直接使用原始名称）
        const ElIconComponent =
          ElementPlusIcons[icon as keyof typeof ElementPlusIcons];
        if (ElIconComponent) {
          // 包装在 el-icon 组件中
          return () => h(ElIcon, { size: 16 }, () => h(ElIconComponent));
        }
        return null;
      }
      return icon;
    };
    const getMenuList = () => {
      Api.getMenuList().then((val) => {
        menuItems.value = val.result;
        nextTick(() => {
          menuItems.value.forEach((element: any) => {
            router.options.routes.forEach((item: any) => {
              if (element.id === item.id) {
                element.url = item.path;
                element.icon = item.icon;
              }
            });
          });
        });
      });
    };
    // 统一 key 格式：去掉前导斜杠
    const normalizeKey = (key: string) => {
      return key.startsWith("/") ? key.substring(1) : key;
    };

    const handleMenuSelect = ({ key }: any) => {
      const normalizedKey = normalizeKey(key);
      selectedKeys.value = [normalizedKey];
      router.push(key);
    };

    // 监听路由变化，更新选中状态
    const updateSelectedKeys = () => {
      const path = route.path;
      if (path && path !== "/") {
        const key = normalizeKey(path);
        selectedKeys.value = [key];
      }
    };

    // 监听路由变化
    watch(
      () => route.path,
      () => {
        updateSelectedKeys();
      }
    );

    onMounted(() => {
      getMenuList();
      updateSelectedKeys();
    });
    return {
      currentPath,
      handleMenuSelect,
      openKeys,
      selectedKeys,
      routerName,
      menuItems,
      getIconComponent,
    };
  },
  components: {
    ElIcon,
  },
});
